import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createLiveEventMetadata, createVideoFormatMetadata, createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { isPlayableLiveEvent } from '@jwp/ott-common/src/utils/liveEvent';
import { canFeedBeUsedAsMediaPlaylist } from '@jwp/ott-common/src/utils/collection';
import env from '@jwp/ott-common/src/env';

import { useTranslationKey } from './useTranslationKey';

export default function useLiveEventData(data: PlaylistItem, id: string, feedId: string | null) {
  const playlistSource: 'feed' | 'recommendations' = env.APP_MEDIA_PLAYLIST_SOURCE || 'recommendations';
  const translationKey = useTranslationKey('title');
  const { t, i18n } = useTranslation(['video', 'common']);

  // Config
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { features, custom } = config;
  const isFavoritesEnabled: boolean = Boolean(features?.favoritesList);
  const isSharingEnabled: boolean = Boolean(!custom?.disableShare);

  // Media
  const playlistId = playlistSource === 'feed' && canFeedBeUsedAsMediaPlaylist(config, feedId) ? feedId : features?.recommendationsPlaylist;
  const { isLoading: isPlaylistLoading, data: playlist } = usePlaylist(
    playlistId || '',
    playlistId === features?.recommendationsPlaylist ? { related_media_id: id } : {},
  );
  const shouldShowPlaylist = playlist && playlist.playlist.length > 1;
  const playlistTitle = shouldShowPlaylist
    ? playlistSource === 'feed'
      ? t('video:recommended')
      : (playlist?.[translationKey] as string) || playlist?.title || ''
    : '';
  const nextItem = useMemo(() => {
    if (!id || !playlist) return;

    const index = playlist.playlist.findIndex(({ mediaid }) => mediaid === id);
    const nextItem = playlist.playlist[index + 1];

    return nextItem;
  }, [id, playlist]);

  // User, entitlement
  const { isEntitled, mediaOffers } = useEntitlement(data);
  const { isLoggedIn, hasSubscription, passes } = useAccountStore(
    ({ user, subscription, passes }) => ({ isLoggedIn: !!user, hasSubscription: !!subscription, passes }),
    shallow,
  );
  const hasMediaOffers = !!mediaOffers.length;

  // UI
  const getCardUrl = (item: PlaylistItem) => mediaURL({ id: item.mediaid, title: item.title, playlistId: config.features?.recommendationsPlaylist });
  const movieURL = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: false });
  const playUrl = mediaURL({ id: data.mediaid, title: data.title, playlistId: feedId, play: true });
  const primaryMetadata = [
    ...createVideoMetadata(
      { ...data, rating: '', pubdate: 0 },
      {
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
  ];
  const liveEventMetadata = createLiveEventMetadata(data.scheduledStart, data.scheduledEnd, i18n.language);

  const videoFormats = createVideoFormatMetadata(data);
  const videoRating = [data?.rating, data?.ratingComponents].filter(Boolean);

  return {
    passes,
    isPlayable: isPlayableLiveEvent(data),
    isLoggedIn,
    hasSubscription,
    playlist,
    playlistTitle,
    isPlaylistLoading,
    hasMediaOffers,
    mediaOffers,
    movieURL,
    playUrl,
    nextItem,
    accessModel,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    liveEventMetadata,
    videoFormats,
    videoRating,
    getCardUrl,
  };
}
